import { Link } from "react-router-dom";
import { ArrowRight, Terminal, Zap, Lock, Cpu, Spade, Users } from "lucide-react";

const ChipStat = ({ value, label, color }) => (
  <div className="panel p-5 card-suit deal" data-suit="♠">
    <div className={`text-3xl font-serif ${color || "text-neutral-100"}`}>{value}</div>
    <div className="label-mono mt-2">{label}</div>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-black text-neutral-100 felt relative overflow-hidden">
      <div className="scanlines absolute inset-0 pointer-events-none" />
      <nav className="relative z-10 px-8 md:px-16 py-6 flex items-center justify-between border-b border-neutral-900">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-red-600/70 flex items-center justify-center bg-black">
            <Terminal size={16} className="text-red-500" />
          </div>
          <span className="font-serif text-2xl tracking-tight">AceHack</span>
          <span className="label-mono ml-2">// pm.suite</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="btn-ghost">Login</Link>
          <Link to="/register" data-testid="nav-register" className="btn-primary btn-glitch">Get In</Link>
        </div>
      </nav>

      <section className="relative z-10 px-8 md:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 deal">
          <div className="label-mono mb-6">
            <span className="text-green-400">{">"}</span> project_mgmt.run --casino --hacker
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter">
            Run your business
            <br />
            like a <span className="glow-red italic">high-stakes</span>
            <br />
            <span className="glow-green">table.</span>
          </h1>
          <p className="mt-8 text-neutral-300 text-sm md:text-base leading-relaxed max-w-xl">
            A minimal project management suite tuned for operators who think in plays, not
            tickets. Track projects, deal tasks across the felt, log time, and chat with your
            own local AI — LM Studio, Ollama, or Ollama Cloud — without leaving the table.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-register" className="btn-primary btn-glitch flex items-center gap-2">
              Deal me in <ArrowRight size={14} />
            </Link>
            <Link to="/login" data-testid="hero-cta-login" className="btn-ghost flex items-center gap-2">
              Existing player
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
            <ChipStat value="07" label="Modules" color="text-amber-400" />
            <ChipStat value="3x" label="AI Providers" color="text-green-400" />
            <ChipStat value="JWT" label="Secure Auth" color="text-red-400" />
            <ChipStat value="0ms" label="Cloud Lock-in" />
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="panel panel-accent-red p-0 relative overflow-hidden deal" style={{ animationDelay: "120ms" }}>
            <img
              src="https://static.prod-images.emergentagent.com/jobs/bf72d466-65db-4239-bf11-d117be4e8f6f/images/6408ec56c4294cc86104e3ca47319840b2030d254bef37de9e2a6e5aa464d94a.png"
              alt="poker chip on motherboard"
              className="w-full h-[420px] object-cover opacity-95"
            />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black via-black/80 to-transparent">
              <div className="label-mono text-green-400">house edge.protocol</div>
              <div className="font-serif text-2xl mt-1">Your data. Your bets.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 px-8 md:px-16 py-20 border-t border-neutral-900">
        <div className="label-mono mb-6">// the.deck</div>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-12">Built for the operator class.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { Icon: Spade, title: "Kanban as Cards", body: "Tasks dealt across To Do, In Play, Closed — with suit accents and priority chips." },
            { Icon: Cpu, title: "Local AI Suite", body: "Bring your own model. LM Studio, Ollama, or Ollama Cloud. Stream answers in a terminal chat." },
            { Icon: Lock, title: "Hardened Auth", body: "JWT + bcrypt, httpOnly cookies, brute-force lockout. Lock the room before you sit down." },
            { Icon: Zap, title: "Time Tracking", body: "Press start. Press stop. Logs roll up by project. No timesheet theater." },
            { Icon: Terminal, title: "Notes that Compile", body: "Markdown notes, taggable, attached to projects. Documentation that reads like a runbook." },
            { Icon: Users, title: "Team & Roles", body: "Owner / admin / member / viewer. Seat assignments, no committee." },
          ].map(({ Icon, title, body }, i) => (
            <div key={title} className="panel p-6 card-suit deal" data-suit={["♠", "♥", "♦", "♣"][i % 4]} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-10 h-10 border border-neutral-800 flex items-center justify-center mb-4">
                <Icon size={16} className="text-amber-400" />
              </div>
              <div className="font-serif text-xl mb-2">{title}</div>
              <p className="text-sm text-neutral-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 px-8 md:px-16 py-10 border-t border-neutral-900 flex items-center justify-between">
        <div className="label-mono">© 2026 AceHack Suite</div>
        <div className="label-mono">all-in.encrypted</div>
      </footer>
    </div>
  );
}
